// Tableau de suivi Feedbaacks : connexion par code, une app à la fois, le
// tableau des retours (le même composant que le widget, en mode équipe) et
// l'administration de l'app.
import { render, type JSX } from 'preact'
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks'
import { createClient, FeedbaacksError, type AppSummary } from '@feedbaacks/sdk'
import { Board, createInstance, FeedbaacksProvider, Icons, Overlays, Spinner, styles, themeCss, type Instance } from '@feedbaacks/ui'
import { d, locale } from './i18n'
import { Keys } from './pages/Keys'
import { Login } from './pages/Login'
import { Members } from './pages/Members'
import { Preferences } from './pages/Preferences'
import { Settings } from './pages/Settings'
import { Superheroes } from './pages/Superheroes'
import { TowerApprovalPage, TowerItemPanel, TowerPage } from './pages/Tower'
import { href, navigate, onLinkClick, useRoute, type Route } from './router'
import { clearToken, readToken } from './session'
import './dashboard.css'

const API = (import.meta.env.VITE_FEEDBAACKS_API as string | undefined) ?? location.origin

// Feuille de style des composants, une fois, dans le document.
const style = document.createElement('style')
style.textContent = styles + themeCss('auto')
document.head.appendChild(style)

function App(): JSX.Element {
  const route = useRoute()
  const [token, setToken] = useState(readToken)
  if (!token || route.name === 'login') {
    return <Login apiUrl={API} onSignedIn={(t) => {
      setToken(t)
      navigate('/', true)
    }} />
  }
  return <Shell token={token} route={route} onSignOut={() => {
    clearToken()
    setToken(null)
    navigate('/login', true)
  }} />
}

function Shell({ token, route, onSignOut }: { token: string; route: Route; onSignOut: () => void }): JSX.Element {
  const [apps, setApps] = useState<AppSummary[] | null>(null)
  const [reload, setReload] = useState(0)
  const appKey = 'app' in route ? route.app : apps?.[0]?.key
  const app = apps?.find((a) => a.key === appKey)
  // Un client et une instance par app : état, filtres et temps réel propres.
  const instance = useMemo<Instance | null>(() => {
    if (!app) return null
    return createInstance({
      client: createClient({ baseUrl: API, auth: { type: 'token', token, app: app.key } }),
      locale,
      role: app.role,
      linkTo: (item) => href({ name: 'feedback', app: app.key, itemId: item.id }),
      // Le panneau de la tour ne s'affiche que pour son équipe (403 sinon).
      slots: { detail: (item) => <TowerItemPanel item={item} /> },
    })
  }, [token, app?.key, app?.role])
  // La tour et l'administration n'appartiennent à aucune app : leur propre client.
  const globalInstance = useMemo(() => createInstance({ client: createClient({ baseUrl: API, auth: { type: 'token', token } }), locale }), [token])
  // null : pas encore su. Le serveur reste seul juge (403 sur chaque route).
  const [superAdmin, setSuperAdmin] = useState<boolean | null>(null)
  useEffect(() => {
    globalInstance.client.account.me().then((me) => setSuperAdmin(me.is_super_admin), () => setSuperAdmin(false))
  }, [globalInstance])
  const [tower, setTower] = useState<number | null>(null)
  useEffect(() => {
    globalInstance.client.tower.counts().then((n) => setTower(n.total), () => setTower(null))
  }, [globalInstance, route.name])
  // E-mails en attente de validation : pastille du lien « Administration ».
  const [pendingEmails, setPendingEmails] = useState(0)
  const refreshPendingEmails = useCallback(() => {
    if (superAdmin) globalInstance.client.admin.emailOutbox.counts().then((n) => setPendingEmails(n.pending), () => setPendingEmails(0))
  }, [globalInstance, superAdmin])
  useEffect(refreshPendingEmails, [refreshPendingEmails, route.name])

  useEffect(() => {
    createClient({ baseUrl: API, auth: { type: 'token', token } }).apps.list().then(setApps, (err: unknown) => {
      if (err instanceof FeedbaacksError && err.status === 401) onSignOut()
      else setApps([])
    })
  }, [token, reload])

  useEffect(() => {
    if (route.name === 'home' && apps?.length) navigate(href({ name: 'feedback', app: apps[0].key }), true)
  }, [route.name, apps])

  // Lien profond ↔ retour déplié.
  useEffect(() => {
    if (!instance || !app) return
    if (route.name === 'feedback' && route.itemId) instance.store.set({ selectedId: route.itemId })
    return instance.store.subscribe((s) => {
      if (route.name !== 'feedback') return
      navigate(href({ name: 'feedback', app: app.key, itemId: s.selectedId ?? undefined }), true)
    })
  }, [instance, route.name])

  if (apps === null) return <div class="dash-center"><Spinner size={24} /></div>
  const isAdmin = app?.role === 'admin'
  const tabs: Array<{ route: Route; label: string; Icon: Icons.Icon; admin?: boolean }> = app
    ? [
        { route: { name: 'feedback', app: app.key }, label: d('nav_feedback'), Icon: Icons.Message },
        { route: { name: 'settings', app: app.key }, label: d('nav_settings'), Icon: Icons.Settings, admin: true },
        { route: { name: 'keys', app: app.key }, label: d('nav_keys'), Icon: Icons.Key, admin: true },
        { route: { name: 'members', app: app.key }, label: d('nav_members'), Icon: Icons.Users, admin: true },
      ]
    : []
  return (
    <div class="fb-root dash">
      <header class="dash-header">
        <a href="/" class="dash-brand" onClick={(e) => onLinkClick(e, '/')}>{d('app_title')}</a>
        {apps.length > 0 && (
          <select class="fb-select" aria-label={d('app_switch')} value={appKey} data-testid="dash-app-switch"
            onChange={(e) => navigate(href({ name: 'feedback', app: e.currentTarget.value }))}>
            {apps.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
          </select>
        )}
        <nav class="dash-tabs">
          {tabs.filter((t) => !t.admin || isAdmin).map((t) => (
            <a key={t.route.name} href={href(t.route)} class={route.name === t.route.name ? 'is-active' : undefined} onClick={(e) => onLinkClick(e, href(t.route))}
              data-testid={`dash-tab-${t.route.name}`}>
              <t.Icon size={14} />
              {t.label}
            </a>
          ))}
        </nav>
        <span class="fb-grow" />
        {tower !== null && (
          <a href="/tower" class={route.name === 'tower' ? 'dash-tower-link is-active' : 'dash-tower-link'} onClick={(e) => onLinkClick(e, '/tower')} data-testid="dash-tab-tower">
            Tour de contrôle{tower > 0 && <span class="fb-chip-count">{tower}</span>}
          </a>
        )}
        {superAdmin && (
          <a href="/superheroes" class={route.name === 'superheroes' ? 'dash-tower-link is-active' : 'dash-tower-link'} onClick={(e) => onLinkClick(e, '/superheroes')} data-testid="dash-tab-superheroes">
            {d('nav_admin')}{pendingEmails > 0 && <span class="fb-chip-count" data-testid="dash-outbox-badge">{pendingEmails}</span>}
          </a>
        )}
        <a href="/preferences" class="dash-icon-link" aria-label={d('nav_preferences')} title={d('nav_preferences')} onClick={(e) => onLinkClick(e, '/preferences')}>
          <Icons.Mail size={16} />
        </a>
        <button type="button" class="dash-icon-link" aria-label={d('logout')} title={d('logout')} onClick={onSignOut} data-testid="dash-logout">
          <Icons.LogOut size={16} />
        </button>
      </header>
      <main class="dash-main">
        {route.name === 'tower' || route.name === 'tower_approve' ? (
          <FeedbaacksProvider instance={globalInstance}>
            {route.name === 'tower' ? <TowerPage superAdmin={superAdmin ?? false} /> : <TowerApprovalPage code={route.code} />}
            <Overlays />
          </FeedbaacksProvider>
        ) : route.name === 'superheroes' ? (
          // Sans le droit, l'adresse n'existe pas : rien ne dit qu'il y a une console.
          superAdmin === null ? (
            <div class="dash-center"><Spinner size={24} /></div>
          ) : superAdmin ? (
            <FeedbaacksProvider instance={globalInstance}>
              <Superheroes apps={apps} onChanged={refreshPendingEmails} />
              <Overlays />
            </FeedbaacksProvider>
          ) : (
            <p class="dash-center fb-muted">{d('not_found')}</p>
          )
        ) : route.name === 'preferences' ? (
          <Preferences client={createClient({ baseUrl: API, auth: { type: 'token', token } })} />
        ) : !apps.length ? (
          <p class="dash-center fb-muted">{d('no_apps')}</p>
        ) : !instance || !app ? (
          <p class="dash-center fb-muted">{d('not_found')}</p>
        ) : (
          <FeedbaacksProvider instance={instance}>
            {route.name === 'feedback' && <div class="dash-board"><Board title={app.name} /></div>}
            {route.name === 'settings' && isAdmin && <Settings appKey={app.key} onRenamed={() => setReload((n) => n + 1)} />}
            {route.name === 'keys' && isAdmin && <Keys appKey={app.key} apiUrl={API} />}
            {route.name === 'members' && isAdmin && <Members appKey={app.key} />}
            <Overlays />
          </FeedbaacksProvider>
        )}
      </main>
    </div>
  )
}

render(<App />, document.getElementById('app')!)
